/**
 * useHoldsStill - should this screen play no motion?
 *
 * The viewer's own setting (`motion`: always / never / follow my system),
 * else the TABLE's default for it (the GM's, a fallback only: plan
 * body-map C2), else "follow my system" - over what the system reports
 * (`prefers-reduced-motion`), followed live. The ONE reader of the media
 * query - no stylesheet asks it; they read `:root[data-motion]`, which
 * MotionAttribute sets from this.
 *
 * @returns {boolean} true: hold still
 */
import { useSyncExternalStore } from 'react';
import { holdsStill } from '../engine/phase/motion';
import { settingByKey, viewerChoice } from '../engine/settings/registry';
import { getViewerPrefs, subscribeViewerPrefs } from '../utils/viewerPrefs';
import { getTableDefaults, subscribeTableDefaults } from '../utils/tableDefaults';

const QUERY = '(prefers-reduced-motion: reduce)';
const MOTION = settingByKey('motion');
// The query's list, made once per `matchMedia`: every subscriber reads it on every render, and making it afresh each
// time cost an orbit about a sixth of a second in ten (perf overhaul 2b). Keyed on the function, so a test's stubbed
// matchMedia still gets its own list (a stub that changes its answer must be a new function, as vi.stubGlobal per
// test is; a real list's `matches` is live).
let cached = { from: null, list: null };
function media() {
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  if (cached.from !== window.matchMedia) cached = { from: window.matchMedia, list: window.matchMedia(QUERY) };
  return cached.list;
}

function subscribeSystem(onChange) {
  const m = media();
  if (!m?.addEventListener) return () => {};
  m.addEventListener('change', onChange);
  return () => m.removeEventListener('change', onChange);
}
const systemAsksForLess = () => !!media()?.matches;

const ownMotion = () => getViewerPrefs().motion;
const tableMotion = () => getTableDefaults().motion;

/**
 * What decides this screen's motion: the viewer's own choice (if any), the table's, and the system - each read as
 * the one value, so a change to any OTHER preference or table default re-renders nobody here.
 */
function useMotionInputs() {
  const own = useSyncExternalStore(subscribeViewerPrefs, ownMotion, ownMotion);
  const table = useSyncExternalStore(subscribeTableDefaults, tableMotion, tableMotion);
  const system = useSyncExternalStore(subscribeSystem, systemAsksForLess, () => false);
  return { own, table, system };
}

export default function useHoldsStill() {
  const { own, table, system } = useMotionInputs();
  return holdsStill(viewerChoice(MOTION, own, { motion: table }), system);
}

/**
 * Whether to ask this viewer (the motion strip): they made no choice of their own, the table plays motion, and their
 * system asks for less - the one case where the table's default overrides what the system says, unasked.
 */
export function useMotionDisagrees() {
  const { own, table, system } = useMotionInputs();
  return own === undefined && viewerChoice(MOTION, undefined, { motion: table }) === 'on' && system;
}
